import React, { useState } from "react";
import { Link, NavLink, Outlet, useNavigate } from "react-router-dom";
import { List, SignOut, CaretDown, ArrowSquareOut, GraduationCap } from "@phosphor-icons/react";
import { Logo } from "./shared";
import { useAuth } from "../context/AuthContext";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "./ui/dropdown-menu";
import { Sheet, SheetContent, SheetTrigger } from "./ui/sheet";
import WalletChip from "./WalletChip";

function NavList({ nav, onNavigate }) {
  return (
    <nav className="space-y-1">
      {nav.map((n) => (
        <NavLink key={n.to} to={n.to} end={n.end} onClick={onNavigate} data-testid={`nav-${n.label.replace(/\s/g, "-").toLowerCase()}`}
          className={({ isActive }) =>
            `flex items-center gap-3 px-3 h-10 rounded-lg text-sm font-medium transition-colors ${isActive ? "bg-foreground text-background" : "text-muted-foreground hover:bg-muted hover:text-foreground"}`}>
          <n.icon size={18} weight="regular" /> {n.label}
        </NavLink>
      ))}
    </nav>
  );
}

export default function DashboardLayout({ nav, kicker, walletChip, tutorialTo }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const initials = (user?.name || user?.email || "U").slice(0, 2).toUpperCase();

  return (
    <div className="min-h-screen bg-background lg:grid lg:grid-cols-[260px_1fr]">
      {/* desktop sidebar */}
      <aside className="hidden lg:flex flex-col border-r border-border bg-card p-4 sticky top-0 h-screen">
        <Link to="/" className="px-1 mb-1"><Logo /></Link>
        <div className="overline px-1 mb-4">{kicker}</div>
        <NavList nav={nav} />
        <div className="flex-1" />
        <Link to="/" className="flex items-center gap-2 px-3 h-10 rounded-lg text-sm text-muted-foreground hover:text-foreground"><ArrowSquareOut size={16} /> Public site</Link>
      </aside>

      <div className="flex flex-col min-h-screen">
        <header className="sticky top-0 z-30 glass border-b border-border h-16 flex items-center px-4 md:px-6 gap-3">
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <button className="lg:hidden w-9 h-9 grid place-items-center rounded-lg border border-border" data-testid="mobile-nav-trigger"><List size={18} /></button>
            </SheetTrigger>
            <SheetContent side="left" className="w-72 p-4">
              <div className="mb-1"><Logo /></div>
              <div className="overline mb-4">{kicker}</div>
              <NavList nav={nav} onNavigate={() => setOpen(false)} />
            </SheetContent>
          </Sheet>
          <div className="lg:hidden"><Logo /></div>
          <div className="flex-1" />
          {tutorialTo && (
            <Link to={tutorialTo} data-testid="header-tutorial-btn"
              className="inline-flex items-center gap-1.5 rounded-full h-9 px-3 text-sm font-semibold border border-border bg-card hover:bg-muted transition-colors btn-press">
              <GraduationCap size={16} weight="fill" /> <span className="hidden sm:inline">Tutorial</span>
            </Link>
          )}
          {walletChip && <WalletChip />}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="flex items-center gap-2 rounded-full border border-border bg-card pl-1 pr-2 h-9 btn-press" data-testid="dash-user-menu">
                <span className="grid place-items-center w-7 h-7 rounded-full bg-foreground text-background text-xs font-bold">{initials}</span>
                <span className="text-sm font-medium hidden sm:block max-w-[140px] truncate">{user?.name}</span>
                <CaretDown size={14} className="text-muted-foreground" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-52">
              <DropdownMenuLabel className="truncate">{user?.email}</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => { logout(); navigate("/"); }} className="text-destructive" data-testid="dash-logout"><SignOut size={16} className="mr-2" /> Logout</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </header>
        <main className="flex-1 p-4 md:p-8"><Outlet /></main>
      </div>
    </div>
  );
}
